import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import './About.css';

const About = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#team') {
      const element = document.getElementById('team');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="about-page">
      <Helmet>
        <title>Acerca de Graphyra | Nuestra Historia</title>
        <meta name="description" content="Conoce la historia detrás de Graphyra, nuestro equipo y nuestra misión de democratizar el conocimiento" />
      </Helmet>

      <div className="about-container">
        {/* Hero Section */}
        <section className="about-hero">
          <div className="hero-content">
            <h1 className="hero-title">Acerca de Graphyra</h1>
            <p className="hero-subtitle">
              Tu ventana al mundo del conocimiento y la creatividad
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="about-section mission-section">
          <div className="section-content">
            <h2 className="section-title">Nuestra Misión</h2>
            <div className="mission-box">
              <p className="mission-text">
                <strong>Democratizar el acceso al conocimiento</strong> y crear un espacio donde las ideas, 
                la creatividad y el aprendizaje se encuentren. Creemos que todos merecen acceso a contenido 
                de calidad que informe, eduque y entretenga.
              </p>
            </div>
          </div>
        </section>

        {/* Story Section */}
        <section className="about-section story-section">
          <div className="section-content">
            <h2 className="section-title">Nuestra Historia</h2>
            <div className="story-content">
              <p>
                Todo comenzó con <strong>"Mi Mundo"</strong>, un prototipo creado por <strong>Luis Cepeda</strong>{' '}
                con una misión clara: <strong>la divulgación científica rigurosa y accesible</strong>. Mi Mundo{' '}
                nació del deseo de acercar la ciencia a todos, hacer el conocimiento científico comprensible{' '}
                y disponible para cualquier persona interesada en aprender. Aunque también buscaba combatir la{' '}
                desinformación, su objetivo principal siempre fue promover la curiosidad científica y el pensamiento{' '}
                crítico. Sin embargo, Mi Mundo nunca vio la luz como proyecto completo.
              </p>
              <p>
                De ese prototipo sin terminar nació algo más grande. <strong>Mi Mundo se transformó en Graphyra</strong>,{' '}
                evolucionando de un enfoque exclusivamente científico a una plataforma integral que abraza{' '}
                <strong>artículos científicos, contenido educativo, entretenimiento inteligente y creatividad</strong>{' '}
                en un solo lugar.
              </p>
              <p>
                Hoy, <strong>Graphyra</strong> es el resultado de esa visión ampliada: <strong>crear una plataforma{' '}
                donde el conocimiento fluya libremente</strong> y las personas puedan descubrir, aprender y compartir{' '}
                sin barreras. Lo que empezó como una idea en una laptop se ha convertido en una plataforma en{' '}
                crecimiento, impulsada por la pasión de crear algo significativo y la convicción de que{' '}
                <strong>la información debe ser accesible para todos</strong>.
              </p>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="about-section team-section" id="team">
          <div className="section-content">
            <h2 className="section-title">Nuestro Equipo</h2>
            
            <div className="team-intro">
              <p>
                Graphyra es el resultado del esfuerzo conjunto de un <strong>pequeño pero dedicado equipo</strong> 
                de estudiantes universitarios y desarrolladores que comparten una visión común.
              </p>
            </div>

            <div className="team-groups">
              <div className="team-group">
                <h3 className="group-title">👨‍💻 Equipo de Desarrollo</h3>
                <div className="group-description">
                  <p>
                    Un grupo de desarrolladores apasionados que transformaron la idea de Graphyra en realidad.{' '}
                    Con experiencia en tecnologías web modernas, arquitectura de software y diseño de experiencia{' '}
                    de usuario, este equipo trabaja constantemente para crear y mejorar cada aspecto de la plataforma,{' '}
                    asegurando que sea robusta, intuitiva y accesible para todos.
                  </p>
                </div>
              </div>

              <div className="team-group">
                <h3 className="group-title">✍️ Equipo de Contenido</h3>
                <div className="group-description">
                  <p>
                    Estudiantes universitarios talentosos de diversas disciplinas que aportan <strong>perspectivas 
                    únicas y contenido de calidad</strong>. Apasionados por la investigación, la escritura y el 
                    aprendizaje continuo, son el corazón creativo de Graphyra.
                  </p>
                  <ul className="team-features">
                    <li>Investigación rigurosa y artículos científicos accesibles</li>
                    <li>Análisis de cultura, sociedad y tendencias actuales</li>
                    <li>Contenido educativo sobre ciencia y tecnología</li>
                    <li>Creatividad y entretenimiento inteligente</li>
                  </ul>
                </div>
              </div>

              <div className="team-group">
                <h3 className="group-title">🚀 Fundador y Visión</h3>
                <div className="group-description">
                  <p>
                    <strong>Luis Cepeda</strong>, CEO y fundador, lidera Graphyra con la visión de crear 
                    una plataforma que inspire, eduque y conecte personas a través del conocimiento. 
                    Con background en desarrollo de software y pasión por la educación, supervisa todos 
                    los aspectos de la plataforma.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="about-section values-section">
          <div className="section-content">
            <h2 className="section-title">Nuestros Valores</h2>
            
            <div className="values-grid">
              <div className="value-card">
                <div className="value-icon">💡</div>
                <h3 className="value-title">Conocimiento Abierto</h3>
                <p className="value-description">
                  Creemos en el acceso libre al conocimiento y en la importancia de compartir ideas 
                  sin barreras innecesarias.
                </p>
              </div>

              <div className="value-card">
                <div className="value-icon">🎯</div>
                <h3 className="value-title">Calidad sobre Cantidad</h3>
                <p className="value-description">
                  Priorizamos contenido bien investigado, verídico y de valor sobre el volumen 
                  o el sensacionalismo.
                </p>
              </div>

              <div className="value-card">
                <div className="value-icon">🤝</div>
                <h3 className="value-title">Comunidad Respetuosa</h3>
                <p className="value-description">
                  Fomentamos un ambiente de respeto, diversidad de pensamiento y conversaciones 
                  constructivas.
                </p>
              </div>

              <div className="value-card">
                <div className="value-icon">🌱</div>
                <h3 className="value-title">Crecimiento Continuo</h3>
                <p className="value-description">
                  Nos comprometemos a mejorar constantemente, aprender de nuestra comunidad y 
                  evolucionar con las necesidades de nuestros usuarios.
                </p>
              </div>

              <div className="value-card">
                <div className="value-icon">🔒</div>
                <h3 className="value-title">Privacidad y Transparencia</h3>
                <p className="value-description">
                  Respetamos tu privacidad y somos transparentes sobre cómo manejamos tus datos 
                  y operamos la plataforma.
                </p>
              </div>

              <div className="value-card">
                <div className="value-icon">🚀</div>
                <h3 className="value-title">Innovación Responsable</h3>
                <p className="value-description">
                  Incorporamos nuevas tecnologías y características de forma responsable, 
                  priorizando el bienestar de nuestra comunidad.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Platform Section */}
        <section className="about-section platform-section">
          <div className="section-content">
            <h2 className="section-title">La Plataforma</h2>
            
            <div className="platform-features">
              <div className="feature-item">
                <h3>📚 Academy</h3>
                <p>
                  Cursos y recursos educativos sobre programación, ciencia de datos, diseño y más. 
                  Aprende de expertos y mejora tus habilidades.
                </p>
              </div>

              <div className="feature-item">
                <h3>📰 Magazine</h3>
                <p>
                  Artículos científicos, ensayos, análisis y reportajes sobre ciencia, tecnología, 
                  cultura y sociedad. Contenido profundo y bien investigado.
                </p>
              </div>

              <div className="feature-item">
                <h3>📺 TV</h3>
                <p>
                  Videos educativos, documentales, charlas y contenido audiovisual de calidad. 
                  Aprende visualmente con series y episodios temáticos.
                </p>
              </div>

              <div className="feature-item">
                <h3>🎮 Games</h3>
                <p>
                  Juegos educativos y de entretenimiento que estimulan el pensamiento crítico 
                  y hacen del aprendizaje una experiencia divertida.
                </p>
              </div>

              <div className="feature-item">
                <h3>😄 Memes & Social</h3>
                <p>
                  Porque no todo es seriedad: comparte memes, interactúa con la comunidad y 
                  disfruta de contenido entretenido con personas que comparten tus intereses.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Join Section */}
        <section className="about-section join-section">
          <div className="section-content">
            <h2 className="section-title">Únete a Nosotros</h2>
            
            <div className="join-content">
              <p className="join-text">
                Graphyra está en sus primeras etapas, pero nuestra visión es grande. Estamos construyendo 
                una plataforma que <strong>cambie la forma en que las personas acceden, comparten y consumen 
                conocimiento</strong>.
              </p>
              
              <div className="join-cards">
                <div className="join-card">
                  <h3>🌟 Usuarios</h3>
                  <p>
                    Únete a nuestra comunidad. Crea una cuenta, comparte tu conocimiento, participa en 
                    discusiones y contribuye al crecimiento de esta plataforma.
                  </p>
                  <Link to="/register" className="join-button">Crear Cuenta</Link>
                </div>

                <div className="join-card">
                  <h3>✍️ Creadores</h3>
                  <p>
                    ¿Tienes conocimiento que compartir? Escribe artículos, crea cursos, comparte videos. 
                    Haz crecer tu audiencia mientras educas a otros.
                  </p>
                  <Link to="/create" className="join-button">Empezar a Crear</Link>
                </div>

                <div className="join-card">
                  <h3>💼 Colaboradores</h3>
                  <p>
                    A medida que crecemos, buscaremos talento apasionado. Por ahora, <strong>mantente 
                    atento</strong> a futuras oportunidades de unirte al equipo.
                  </p>
                  <Link to="/careers" className="join-button secondary">Más Información</Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="about-section contact-section">
          <div className="section-content">
            <h2 className="section-title">Mantengámonos en Contacto</h2>
            
            <div className="contact-info">
              <p className="contact-intro">
                Nos encantaría escucharte. Ya sea para sugerencias, colaboraciones, reportes o 
                simplemente para saludar.
              </p>

              <div className="contact-grid">
                <div className="contact-card">
                  <div className="contact-icon">📧</div>
                  <h3>Contacto General</h3>
                  <a href="mailto:contact.graphyra@gmail.com">contact.graphyra@gmail.com</a>
                </div>

                <div className="contact-card">
                  <div className="contact-icon">🛠️</div>
                  <h3>Soporte Técnico</h3>
                  <a href="mailto:support.graphyra@gmail.com">support.graphyra@gmail.com</a>
                </div>

                <div className="contact-card">
                  <div className="contact-icon">🏢</div>
                  <h3>Administrativo</h3>
                  <a href="mailto:admin.graphyra@gmail.com">admin.graphyra@gmail.com</a>
                </div>

                <div className="contact-card">
                  <div className="contact-icon">📞</div>
                  <h3>Teléfono</h3>
                  <a href="tel:+573115491733">+57 3115491733</a>
                </div>
              </div>

              <div className="contact-links">
                <Link to="/contact" className="contact-button">Página de Contacto</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Footer Links */}
        <div className="about-footer">
          <div className="footer-links">
            <Link to="/terms">Términos y Condiciones</Link>
            <span className="separator">•</span>
            <Link to="/privacy">Política de Privacidad</Link>
            <span className="separator">•</span>
            <Link to="/guidelines">Normas de la Comunidad</Link>
            <span className="separator">•</span>
            <Link to="/">Volver al inicio</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
